import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { cn } from '../utils/cn';
import { useSpacing } from '../hooks/use-spacing';
import { cva } from 'class-variance-authority';
const cardVariants = cva('relative overflow-hidden transition-all duration-300', {
    variants: {
        variant: {
            // Clean, professional card with enhanced hover
            default: 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-lg hover:border-gray-300 dark:hover:border-gray-700',
            // Elevated card with lift effect
            elevated: 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-md hover:shadow-xl hover:-translate-y-1 hover:border-primary-200 dark:hover:border-primary-800',
            // Interactive card with subtle feedback
            interactive: 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-lg hover:-translate-y-1 hover:scale-[1.02] cursor-pointer hover:border-primary-300 dark:hover:border-primary-700',
            // Glass morphism effect
            glass: 'glass backdrop-blur-lg border border-white/30 dark:border-gray-700/50 shadow-lg hover:shadow-xl hover:bg-white/90 dark:hover:bg-gray-900/90',
            // Floating card with shadow
            floating: 'bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-lg hover:shadow-2xl hover:-translate-y-2 hover:rotate-1',
            // Outlined card
            outlined: 'bg-transparent border-2 border-gray-300 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-600 hover:bg-gray-50/50 dark:hover:bg-gray-800/50',
            // Filled background
            filled: 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-750 hover:shadow-md',
            // Gradient card
            gradient: 'bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border border-primary-200 dark:border-primary-800 hover:shadow-lg hover:-translate-y-0.5 animate-gradient-shift',
        },
        radius: {
            none: 'rounded-none',
            sm: 'rounded-md',
            default: 'rounded-lg',
            lg: 'rounded-xl',
            xl: 'rounded-2xl',
        },
        padding: {
            none: '',
            sm: 'p-4',
            default: 'p-6',
            lg: 'p-8',
        },
    },
    defaultVariants: {
        variant: 'default',
        radius: 'default',
        padding: 'default',
    },
});
const Card = React.forwardRef(({ className, children, variant, radius, padding, ...props }, ref) => {
    return (_jsx("div", { ref: ref, className: cn(cardVariants({ variant, radius, padding }), className), ...props, children: children }));
});
Card.displayName = 'Card';
const CardHeader = React.forwardRef(({ className, ...props }, ref) => {
    const { mode: spacingMode } = useSpacing();
    const spacingClasses = {
        compact: 'p-4',
        normal: 'p-6',
        spacious: 'p-8'
    };
    return (_jsx("div", { ref: ref, className: cn('flex flex-col space-y-1.5', spacingClasses[spacingMode], className), ...props }));
});
CardHeader.displayName = 'CardHeader';
const CardTitle = React.forwardRef(({ className, ...props }, ref) => (_jsx("h3", { ref: ref, className: cn('text-xl font-semibold leading-none tracking-tight text-gray-900 dark:text-gray-100', className), ...props })));
CardTitle.displayName = 'CardTitle';
const CardDescription = React.forwardRef(({ className, ...props }, ref) => (_jsx("p", { ref: ref, className: cn('text-sm text-theme-secondary', className), ...props })));
CardDescription.displayName = 'CardDescription';
const CardContent = React.forwardRef(({ className, ...props }, ref) => {
    const { mode: spacingMode } = useSpacing();
    const spacingClasses = {
        compact: 'p-4 pt-0',
        normal: 'p-6 pt-0',
        spacious: 'p-8 pt-0'
    };
    return (_jsx("div", { ref: ref, className: cn(spacingClasses[spacingMode], className), ...props }));
});
CardContent.displayName = 'CardContent';
const CardFooter = React.forwardRef(({ className, ...props }, ref) => {
    const { mode: spacingMode } = useSpacing();
    const spacingClasses = {
        compact: 'p-4 pt-0',
        normal: 'p-6 pt-0',
        spacious: 'p-8 pt-0'
    };
    return (_jsx("div", { ref: ref, className: cn('flex items-center', spacingClasses[spacingMode], className), ...props }));
});
CardFooter.displayName = 'CardFooter';
export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, cardVariants };
